import { Fragment, useMemo } from "react"
import { useLocation, Link } from "react-router-dom"
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { ROUTES } from "@/constants/routes"

// Route to title mapping
const routeTitleMap: Record<string, string> = {
  [ROUTES.HOME]: "Dashboard",
  [ROUTES.CAMPAIGN_MANAGEMENT]: "Campaign Management",
  [ROUTES.CAMPAIGN_CREATE]: "Create Campaign",
  [ROUTES.CAMPAIGN_VIEW]: "Edit Campaign",
  [ROUTES.SUPPLEMENT_DATA]: "Supplement Data",
  [ROUTES.WEBCHAT]: "Webchat",
  [ROUTES.PRESCREENING]: "Recruitments",
  [ROUTES.PRESCREENING_EDIT]: "Edit Outreach",
  [ROUTES.PRESCREENING_DETAILS]: "Outreach Details",
  [ROUTES.TESTING_CANDIDATE_MGMT]: "Candidate Management",
  [ROUTES.TESTING_CANDIDATE_MGMT_CREATE]: "Create Candidate",
  [ROUTES.TESTING_CANDIDATE_MGMT_EDIT]: "Edit Candidate",
  [ROUTES.TESTING_JOB_ORDER_MGMT]: "Job Order Management",
  [ROUTES.TESTING_JOB_ORDER_CREATE]: "Create Job Order",
  [ROUTES.TESTING_JOB_ORDER_EDIT]: "Edit Job Order",
  [ROUTES.TESTING_JOB_ORDER_RECIPIENTS]: "Job Order Recipients",
  [ROUTES.FILE_MANAGEMENT]: "File Management",
  [ROUTES.KNOWLEDGE_BASE_SETUP]: "Knowledge Base Setup",
  [ROUTES.SENDER_PERSONA]: "Sender Persona",
  [ROUTES.SENDER_PERSONA_FORM]: "Sender Persona Form",
  [ROUTES.SENDER_PERSONA_FORM_EDIT]: "Edit Sender Persona",
  [ROUTES.SENDER_PERSONA_DETAILS]: "Sender Persona Details",
  [ROUTES.COMMUNICATION_INTEGRATION]: "Phone Number Mapping",
}

export function AppBreadcrumb() {
  const location = useLocation()
  const pathname = location.pathname

  // Build breadcrumb items based on the current path
  const getBreadcrumbItems = () => {
    const items: Array<{ label: string; path: string; isActive: boolean }> = []

    // Always start with Home
    items.push({
      label: "Home",
      path: ROUTES.HOME,
      isActive: pathname === ROUTES.HOME,
    })

    // Special handling for campaign view/edit page (with ID)
    if (pathname.match(/^\/campaign-management\/\d+$/)) {
      items.push({
        label: "Campaign Management",
        path: ROUTES.CAMPAIGN_MANAGEMENT,
        isActive: false,
      })
      items.push({
        label: "Campaign Details",
        path: pathname,
        isActive: true,
      })
      return items
    }

    // Special handling for candidate-outreach details page
    if (pathname.startsWith('/candidate-outreach/details/')) {
      items.push({
        label: "Recruitments",
        path: ROUTES.PRESCREENING,
        isActive: false,
      })
      items.push({
        label: "Recruitment Details",
        path: pathname,
        isActive: true,
      })
      return items
    }

    // Special handling for sender persona form pages
    if (pathname === '/sender-persona/form') {
      items.push({
        label: "Sender Persona",
        path: ROUTES.SENDER_PERSONA,
        isActive: false,
      })
      items.push({
        label: "Create Sender Persona",
        path: pathname,
        isActive: true,
      })
      return items
    }

    if (pathname.match(/^\/sender-persona\/form\/\d+$/)) {
      items.push({
        label: "Sender Persona",
        path: ROUTES.SENDER_PERSONA,
        isActive: false,
      })
      items.push({
        label: "Edit Sender Persona",
        path: pathname,
        isActive: true,
      })
      return items
    }

    // Special handling for sender persona details page
    if (pathname.match(/^\/sender-persona\/details\/\d+$/)) {
      items.push({
        label: "Sender Persona",
        path: ROUTES.SENDER_PERSONA,
        isActive: false,
      })
      items.push({
        label: "Sender Persona Details",
        path: pathname,
        isActive: true,
      })
      return items
    }

    // Special handling for file management page
    if (pathname === ROUTES.FILE_MANAGEMENT || pathname.startsWith('/file-management')) {
      items.push({
        label: "Knowledge Base Setup",
        path: ROUTES.KNOWLEDGE_BASE_SETUP,
        isActive: false,
      })
      items.push({
        label: "File Management",
        path: pathname,
        isActive: true,
      })
      return items
    }

    // If not on home page, add current page
    if (pathname !== ROUTES.HOME) {
      const currentPageTitle = routeTitleMap[pathname] ||
        pathname.split('/').pop()?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) ||
        "Page"

      items.push({
        label: currentPageTitle,
        path: pathname,
        isActive: true,
      })
    }

    return items
  }

  const breadcrumbItems = useMemo(() => getBreadcrumbItems(), [pathname])

  return (
    <Breadcrumb>
      <BreadcrumbList className="flex items-center gap-2">
        {breadcrumbItems.map((item, index) => (
          <Fragment key={item.path}>
            <BreadcrumbItem>
              {item.isActive ? (
                <BreadcrumbPage className="text-foreground">
                  {item.label}
                </BreadcrumbPage>
              ) : (
                <BreadcrumbLink asChild>
                  <Link to={item.path} className="text-muted-foreground hover:text-foreground">
                    {item.label}
                  </Link>
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
            {index < breadcrumbItems.length - 1 && (
              <BreadcrumbSeparator>
                <span className="text-muted-foreground">/</span>
              </BreadcrumbSeparator>
            )}
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  )
}
